/**
 * MainLayout.js
 * 정보 아키텍처 기반 메인 레이아웃
 * - 좌측: 분석 중심 메뉴 (Main)
 * - 우측: 도구 패널 (Tool)
 * - 중앙: 컨텐츠 영역
 */

import React, { useState } from 'react';
import { Box, CssBaseline } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import TopBar from './TopBar';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import StatusBar from '../components/StatusBar';
import TaskStatusPanel from '../components/TaskStatusPanel';
import { useMobileView } from '../contexts/MobileViewContext';
import { detectMobileDevice } from '../utils/deviceDetection';

const DRAWER_WIDTH = 215; // 15% 축소 (기존 250에서)
const DRAWER_COLLAPSED_WIDTH = 60; // LeftSidebar의 COLLAPSED_WIDTH와 일치
const RIGHT_SIDEBAR_WIDTH = 400; // RightSidebar의 SIDEBAR_WIDTH와 일치
const RIGHT_SIDEBAR_COLLAPSED_WIDTH = 56;
const TOP_BAR_HEIGHT = 64;

const MainLayout = () => {
  const { isMobileView } = useMobileView();
  const location = useLocation();
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true); // 기본적으로 열린 상태
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true); // 데스크탑 사이드바 토글
  
  // 모바일 경로(/m/*)에서는 MainLayout을 사용하지 않아야 함
  // 하지만 혹시 모를 경우를 대비해 모바일 경로에서는 사이드바 비활성화
  const isMobilePath = location.pathname.startsWith('/m/');
  const [isMobileDevice, setIsMobileDevice] = React.useState(() => detectMobileDevice());
  
  // 화면 크기 변경 시 모바일 디바이스 재감지
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobileDevice(detectMobileDevice());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 모바일 뷰, 모바일 경로, 또는 모바일 디바이스일 때 사이드바 완전히 숨김
  const shouldHideSidebars = isMobileView || isMobilePath || isMobileDevice;
  
  // 모바일 뷰일 때 우측 사이드바는 항상 숨김
  React.useEffect(() => {
    if (shouldHideSidebars) {
      setRightSidebarOpen(false);
      setSidebarOpen(false);
      setMobileOpen(false);
    }
  }, [shouldHideSidebars]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleRightSidebarToggle = () => {
    setRightSidebarOpen(!rightSidebarOpen);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      
      {/* Top Bar */}
      <TopBar 
        onDrawerToggle={handleDrawerToggle}
        onSidebarToggle={handleSidebarToggle}
        sidebarOpen={sidebarOpen}
        drawerWidth={DRAWER_WIDTH}
      />
      
      {/* Left Sidebar - 분석 메뉴 */}
      {/* 모바일 뷰, 모바일 경로, 또는 모바일 디바이스일 때는 완전히 숨김 */}
      {!shouldHideSidebars && (
        <LeftSidebar
          drawerWidth={DRAWER_WIDTH}
          topBarHeight={TOP_BAR_HEIGHT}
          mobileOpen={false}
          sidebarOpen={sidebarOpen}
          onDrawerToggle={handleDrawerToggle}
          onSidebarToggle={handleSidebarToggle}
          forceMobile={false}
        />
      )}
      
      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          mt: `${TOP_BAR_HEIGHT}px`,
          // ml 제거! - width 계산만으로 공간 확보 (이중 계산 방지)
          // mr 제거! - width 계산만으로 공간 확보 (이중 계산 방지)
          width: {
            xs: '100%',
            sm: isMobileView 
              ? '100%' 
              : `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH}px - ${rightSidebarOpen ? RIGHT_SIDEBAR_WIDTH : RIGHT_SIDEBAR_COLLAPSED_WIDTH}px)`
          },
          maxWidth: {
            xs: '100%',
            sm: isMobileView 
              ? '100%' 
              : `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH}px - ${rightSidebarOpen ? RIGHT_SIDEBAR_WIDTH : RIGHT_SIDEBAR_COLLAPSED_WIDTH}px)`
          },
          minWidth: 0,
          boxSizing: 'border-box',
          minHeight: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
          maxHeight: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
          overflow: 'auto',
          pb: 6,
          transition: theme => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          scrollbarWidth: 'thin',
          scrollbarColor: theme => `${theme.palette.divider} transparent`,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme => theme.palette.divider,
            borderRadius: '4px',
            '&:hover': {
              background: theme => theme.palette.action.hover,
            },
          },
        }}
      >
        {/* React Router Outlet - 페이지 컨텐츠가 여기에 렌더링됨 */}
        <Outlet />
      </Box>
      
      {/* Right Sidebar - 모바일 뷰 또는 모바일 경로에서는 숨김 */}
      {!shouldHideSidebars && (
        <RightSidebar
          open={rightSidebarOpen}
          onToggle={handleRightSidebarToggle}
        />
      )}

      {/* Task Status Panel - 우측 하단 작업 패널 */}
      <TaskStatusPanel />

      {/* Status Bar - 하단 상태 표시줄 */}
      <StatusBar />
    </Box>
  );
};

export default MainLayout;

